import { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SessionBatchListItem {
  key: string | number;
  /** Company name — the line the operator scans for. */
  primary: string;
  /** Contact, city, whatever identifies the row at a glance. */
  secondary?: string | null;
}

interface SessionBatchListProps {
  items: SessionBatchListItem[];
  /** Cursor into `items`. Everything before it has been dealt with. */
  currentIndex: number;
  /** Rows already dealt with, when that isn't simply `currentIndex`.
   *  Corrections drop a row from the list as it closes and leave the
   *  cursor where it is, so there the two numbers differ. */
  processed?: number;
  /** Size of the lot as it was handed over, when `items` no longer holds
   *  all of it for the same reason. */
  total?: number;
  accent: "indigo" | "amber";
}

const ACCENTS = {
  indigo: {
    current: "bg-indigo-50 text-indigo-900 ring-1 ring-inset ring-indigo-200",
    marker: "bg-indigo-500",
  },
  amber: {
    current: "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200",
    marker: "bg-amber-500",
  },
} as const;

/** The whole reserved lot, as a rail beside the card.
 *
 *  It exists because lots are no longer 20 rows. At 200 or 500 an operator
 *  needs to see where they are in the batch and what is coming without
 *  scrolling the page, so the list scrolls inside itself and the page does
 *  not: the card next to it stays put while this column moves.
 *
 *  Read-only on purpose. Clicking to jump would need a second cursor
 *  concept — rows are worked in order, and skipping is what moves past
 *  one — so the list reports position rather than setting it. */
export function SessionBatchList({
  items,
  currentIndex,
  processed,
  total,
  accent,
}: SessionBatchListProps) {
  const styles = ACCENTS[accent];
  const currentRef = useRef<HTMLLIElement>(null);

  // Keep the cursor visible. With 500 rows the current one is off-screen
  // within a couple of saves otherwise, which defeats the point of the
  // rail. `nearest` so it doesn't yank the column on every row.
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest" });
  }, [currentIndex]);

  const size = total ?? items.length;
  const done = Math.min(processed ?? currentIndex, size);

  return (
    <aside className="rounded-plane bg-white shadow-plane">
      <div className="flex items-baseline justify-between border-b border-slate-900/[0.06] px-4 py-3">
        <h3 className="text-sm font-medium text-slate-700">Your batch</h3>
        <span className="text-xs tabular-nums text-slate-400">
          {done} / {size}
        </span>
      </div>
      <ul className="max-h-[calc(100vh-16rem)] overflow-y-auto py-1">
        {items.map((item, i) => {
          const isDone = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <li
              key={item.key}
              ref={isCurrent ? currentRef : undefined}
              className={cn(
                "mx-1 flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm",
                isCurrent && styles.current,
                isDone && "text-slate-400",
                !isCurrent && !isDone && "text-slate-600",
              )}
            >
              <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-slate-400">
                {i + 1}
              </span>
              <span className="flex w-4 shrink-0 justify-center">
                {isDone ? (
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                ) : isCurrent ? (
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full", styles.marker)}
                  />
                ) : null}
              </span>
              <span className="min-w-0 flex-1 truncate">
                <span className={cn(isCurrent && "font-medium")}>
                  {item.primary}
                </span>
                {item.secondary && (
                  <span className="ml-1.5 text-xs text-slate-400">
                    {item.secondary}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
